import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Download, FileText, Clock, AlertTriangle, ArrowDownToLine,
  Eye, EyeOff, Lock, Loader2, FolderOpen,
} from "lucide-react";
import XLSyncLogo from "@/components/brand/XLSyncLogo";
import ImageViewer from "@/pages/ImageViewer";
import { fetchPublicShare, publicFetchBlob, shareFileServeUrl } from "@/lib/shareApi";
import { formatPatientDisplayUhid } from "@/lib/patientDisplay";
import { getModalityBadgeClass, getModalityChartColor, getModalityDisplayLabel } from "@/lib/modalities";
import {
  fetchPublicTransfer,
  isTransferIncorrectPassword,
  isTransferPasswordRequired,
  transferDownloadAllUrl,
  transferFileDownloadUrl,
} from "@/lib/transferApi";
import JSZip from "jszip";

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function filesFromSharePayload(data) {
  if (!data) return [];
  if (Array.isArray(data.files) && data.files.length) {
    return data.files.map((f) => ({
      ...f,
      study_id: f.study_id || data.study?.id,
      original_filename: f.original_filename || f.filename,
    }));
  }
  return (data.studies || []).flatMap((s) => {
    const files = s.files || [];
    const reports = (s.reports || []).map((r) => ({
      id: r.id,
      original_filename: r.original_filename || r.filename || "report.pdf",
      filename: r.original_filename || r.filename,
      file_size: r.file_size,
      file_type: "pdf",
      mime_type: r.mime_type || "application/pdf",
      study_id: s.id,
    }));
    return [...files, ...reports].map((f) => ({
      ...f,
      study_id: f.study_id || s.id,
      modality: f.modality || s.modality,
      original_filename: f.original_filename || f.filename,
    }));
  });
}

function studyAttachments(data, study) {
  const all = filesFromSharePayload(data);
  if (!study?.id) return all;
  const matched = all.filter((f) => f.study_id === study.id);
  if (matched.length) return matched;
  if (data?.study?.id === study.id) return all;
  return study.files || [];
}

const safeZipName = (name, fallback = "file") => {
  const text = String(name || fallback).replace(/[^\w.\-]+/g, "_");
  return text || fallback;
};

const triggerBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const fileZipEntries = (token, files, folder = "") => {
  const prefix = folder ? `${folder}/` : "";
  const entries = [];
  (files || []).forEach((file, fileIdx) => {
    if (!file?.id) return;
    const baseName = safeZipName(file.original_filename || file.filename || `file-${fileIdx + 1}`);
    if (file.series_type === "multi_file") {
      const sliceCount = file.slice_files?.length || file.slice_count || 0;
      const seriesFolder = `${prefix}${baseName.replace(/\.[^.]+$/, "") || "series"}`;
      for (let i = 0; i < sliceCount; i += 1) {
        const sliceName = file.slice_files?.[i]?.filename || `slice_${String(i + 1).padStart(4, "0")}.dcm`;
        entries.push({
          path: `${seriesFolder}/${safeZipName(sliceName)}`,
          url: shareFileServeUrl(token, file.id, i),
        });
      }
      return;
    }
    entries.push({
      path: `${prefix}${baseName}`,
      url: shareFileServeUrl(token, file.id),
    });
  });
  return entries;
};

const downloadZipFromEntries = async (entries, zipName) => {
  if (!entries.length) throw new Error("No files to download");
  const zip = new JSZip();
  let added = 0;
  for (const entry of entries) {
    try {
      const blob = await publicFetchBlob(entry.url);
      zip.file(entry.path, blob);
      added += 1;
    } catch {
      // skip missing files
    }
  }
  if (!added) throw new Error("No files could be downloaded");
  const out = await zip.generateAsync({ type: "blob" });
  triggerBlobDownload(out, zipName);
};

function isMissingValue(value) {
  if (value === null || value === undefined) return true;
  const text = String(value).trim();
  if (!text) return true;
  const lower = text.toLowerCase();
  return lower === "na" || lower === "n/a" || lower === "null" || lower === "none";
}

function formatAgeDob(age, dateOfBirth) {
  const parts = [];
  if (!isMissingValue(age)) parts.push(`${age} yrs`);
  if (!isMissingValue(dateOfBirth)) parts.push(dateOfBirth);
  return parts.length ? parts.join(" · ") : null;
}

function ShareInfoField({ label, value, alwaysShow = false }) {
  if (!alwaysShow && isMissingValue(value)) return null;
  const display = isMissingValue(value) ? "—" : value;
  return (
    <div className="min-w-0 text-left">
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold mt-1 break-words">{display}</p>
    </div>
  );
}

function ShareStudyList({ data, token, onOpenStudy }) {
  const patient = data?.patient;
  const shareLink = data?.share_link;
  const studies = data?.studies?.length
    ? data.studies
    : (data?.study ? [data.study] : []);
  const expiresDate = shareLink?.expires_at ? new Date(shareLink.expires_at) : null;
  const [downloadingStudyId, setDownloadingStudyId] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  const handleDownloadStudy = async (study) => {
    if (!token || !study?.id) return;
    const files = studyAttachments(data, study);
    setDownloadingStudyId(study.id);
    try {
      const folder = safeZipName(
        [study.study_date, study.modality, study.id?.slice(0, 8)].filter(Boolean).join("_"),
        "study",
      );
      await downloadZipFromEntries(
        fileZipEntries(token, files),
        `${folder}_files.zip`,
      );
      toast.success("Download started");
    } catch {
      toast.error("No files available to download");
    } finally {
      setDownloadingStudyId(null);
    }
  };

  const handleDownloadAllStudies = async () => {
    if (!token) return;
    setDownloadingAll(true);
    try {
      const entries = studies.flatMap((study) => {
        const files = studyAttachments(data, study);
        const folder = safeZipName(
          [study.study_date, study.modality, study.id?.slice(0, 8)].filter(Boolean).join("_"),
          "study",
        );
        return fileZipEntries(token, files, folder);
      });
      await downloadZipFromEntries(entries, "shared_studies.zip");
      toast.success("Download started");
    } catch {
      toast.error("No files available to download");
    } finally {
      setDownloadingAll(false);
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto space-y-4 px-4 sm:px-6 pb-8" data-testid="shared-study-list">
      <Card>
        <CardContent className="px-5 py-5 sm:px-6">
          <div className="flex items-start gap-3">
            {patient ? (
              <div
                className="w-11 h-11 sm:w-12 sm:h-12 rounded-full flex-shrink-0 grid place-items-center text-sm font-bold text-white bg-[var(--xl-black)]"
                aria-hidden
              >
                {(patient.name || "?")
                  .split(/\s+/)
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((p) => p[0])
                  .join("")
                  .toUpperCase()}
              </div>
            ) : (
              <FolderOpen className="w-5 h-5 text-[var(--xl-primary)] flex-shrink-0 mt-0.5" />
            )}
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl font-semibold" data-testid="shared-listing-title">
                {patient?.name || data?.study?.description || "Shared studies"}
              </h1>
              {patient && (
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <p className="text-sm font-mono text-[var(--xl-primary)]">
                    {formatPatientDisplayUhid(patient)}
                  </p>
                  {(patient.tags || []).map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs font-normal">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-3">
                <span>{studies.length} stud{studies.length === 1 ? "y" : "ies"}</span>
                {expiresDate && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Expires {expiresDate.toLocaleDateString()}
                  </span>
                )}
                {shareLink?.created_by && <span>Shared by {shareLink.created_by}</span>}
              </div>
            </div>
          </div>

          {patient && (
            <div className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-3 mt-5 pt-4 border-t border-border">
              <ShareInfoField label="Age" value={formatAgeDob(patient.age, patient.date_of_birth)} alwaysShow />
              <ShareInfoField label="Gender" value={patient.gender} alwaysShow />
              <ShareInfoField label="Doctor" value={patient.referring_doctor || patient.physician_name} />
              <ShareInfoField label="Hospital" value={patient.institution_name} />
              <ShareInfoField label="Organization" value={patient.organization} />
              <ShareInfoField label="Blood group" value={patient.blood_group} />
              <ShareInfoField label="Contact" value={patient.contact} />
              <ShareInfoField label="Email" value={patient.email} />
            </div>
          )}
        </CardContent>
      </Card>

      {studies.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            No studies on this share
          </CardContent>
        </Card>
      ) : (
        <Card className="flex flex-col min-w-0 overflow-hidden" data-testid="shared-studies-table">
          <CardHeader className="pb-2 pt-4 flex flex-row items-center justify-between gap-2 shrink-0">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              All Studies ({studies.length})
            </CardTitle>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 gap-1.5 text-xs"
              onClick={handleDownloadAllStudies}
              disabled={downloadingAll}
              data-testid="download-all-shared-studies-btn"
            >
              {downloadingAll ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              Download all
            </Button>
          </CardHeader>
          <CardContent className="flex-1 p-0 pb-3 min-h-0">
            <div className="overflow-auto max-h-[min(28rem,calc(100vh-22rem))]">
              <Table className="table-fixed w-full min-w-[560px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Modality</TableHead>
                    <TableHead className="w-[8.5rem]">CT Reviewer</TableHead>
                    <TableHead className="w-[7.5rem]">Date</TableHead>
                    <TableHead className="w-16">Files</TableHead>
                    <TableHead className="w-[5.5rem]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {studies.map((study) => {
                    const files = studyAttachments(data, study);
                    const reviewer = String(study.ct_reviewer ?? "").trim();
                    return (
                      <TableRow key={study.id} data-testid={`shared-listing-study-${study.id}`}>
                        <TableCell>
                          <div className="flex flex-col gap-1 min-w-0">
                            <Badge className={`text-xs w-fit max-w-full gap-1.5 font-normal border-transparent ${getModalityBadgeClass(study.modality)}`}>
                              <span
                                className="w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: getModalityChartColor(study.modality) }}
                                aria-hidden
                              />
                              <span className="truncate">
                                {getModalityDisplayLabel(study.modality)}
                              </span>
                            </Badge>
                            {study.description && (
                              <p className="text-xs text-muted-foreground truncate" title={study.description}>
                                {study.description}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="truncate" title={reviewer || undefined}>
                            {reviewer || "—"}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap">
                          {study.study_date || "—"}
                        </TableCell>
                        <TableCell className="tabular-nums">{files.length}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-0.5">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={() => onOpenStudy(study)}
                            data-testid={`open-shared-study-${study.id}`}
                            aria-label={`View ${getModalityDisplayLabel(study.modality) || "study"}`}
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={() => handleDownloadStudy(study)}
                            disabled={downloadingStudyId === study.id || downloadingAll}
                            data-testid={`download-shared-study-${study.id}`}
                            aria-label={`Download ${getModalityDisplayLabel(study.modality) || "study"}`}
                            title="Download"
                          >
                            {downloadingStudyId === study.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function TransferPasswordGate({ passwordError, submitting, onSubmit }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(password);
  };

  return (
    <div className="min-h-screen bg-[var(--xl-bg)] flex items-center justify-center px-4">
      <Card className="w-full max-w-md" data-testid="transfer-password-gate">
        <CardContent className="p-8">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="mb-4" data-testid="shared-brand">
              <XLSyncLogo size="lg" />
            </div>
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <Lock className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-lg font-semibold mb-1">Password required</h2>
            <p className="text-sm text-muted-foreground">
              This transfer is protected. Enter the password shared by the sender to continue.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="transfer-access-password" className="text-xs text-muted-foreground">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  id="transfer-access-password"
                  data-testid="transfer-access-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Enter transfer password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-10 text-sm pl-9 pr-9"
                  disabled={submitting}
                  autoFocus
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  disabled={submitting}
                >
                  {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            {passwordError && (
              <div
                data-testid="transfer-password-error"
                className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2"
              >
                {passwordError}
              </div>
            )}

            <Button
              type="submit"
              data-testid="transfer-password-submit"
              disabled={submitting || !password.trim()}
              className="w-full h-11 rounded-[10px] font-semibold shadow-[0_5px_14px_var(--xl-primary-shadow)]"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Continue"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function TransferView({ transfer, token, password }) {
  const downloadFile = (fileId, filename) => {
    const url = transferFileDownloadUrl(token, fileId, password);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    toast.success(`Downloading ${filename}`);
  };

  const downloadAll = () => {
    const url = transferDownloadAllUrl(token, password);
    const a = document.createElement("a");
    a.href = url;
    a.download = "transfer.zip";
    a.click();
    toast.success("Downloading all files as ZIP");
  };

  const expiresDate = new Date(transfer.expires_at);
  const isExpired = expiresDate < new Date();

  return (
    <Card>
      <CardContent className="p-0">
        <div className="px-6 py-5 border-b border-border">
          <h1 className="text-lg font-semibold mb-1" data-testid="transfer-title">
            {transfer.title}
          </h1>
          {transfer.message && (
            <p className="text-sm text-muted-foreground mb-3" data-testid="transfer-message">{transfer.message}</p>
          )}
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            {transfer.sender_email && <span>From: {transfer.sender_email}</span>}
            <span>{transfer.file_count} file{transfer.file_count !== 1 ? "s" : ""}</span>
            <span>{formatBytes(transfer.total_size)}</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Expires {expiresDate.toLocaleDateString()}
            </span>
          </div>
        </div>

        <div className="divide-y divide-border">
          {(transfer.files || []).map((f) => (
            <div
              key={f.id}
              className="px-6 py-3 flex items-center gap-3 hover:bg-muted/30 transition-colors"
              data-testid={`file-item-${f.id}`}
            >
              <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{f.original_filename}</p>
                <p className="text-[11px] text-muted-foreground">{formatBytes(f.file_size)}</p>
              </div>
              <button
                onClick={() => downloadFile(f.id, f.original_filename)}
                className="p-1.5 rounded-md hover:bg-secondary transition-colors flex-shrink-0"
                data-testid={`download-file-${f.id}`}
                title="Download"
              >
                <Download className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          ))}
        </div>

        <div className="px-6 py-4 border-t border-border">
          <Button
            data-testid="download-all-btn"
            onClick={downloadAll}
            disabled={isExpired}
            className="w-full h-11 rounded-[10px] font-semibold shadow-[0_5px_14px_var(--xl-primary-shadow)] gap-2"
          >
            <ArrowDownToLine className="w-4 h-4" />
            Download All ({formatBytes(transfer.total_size)})
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SharedDownload() {
  const { token } = useParams();
  const [payload, setPayload] = useState(null);
  const [mode, setMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [transferPassword, setTransferPassword] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [viewingStudy, setViewingStudy] = useState(null);

  const loadTransfer = async (password = null) => {
    try {
      const data = await fetchPublicTransfer(token, password);
      setPayload(data);
      setMode("transfer");
      setPasswordRequired(false);
      setPasswordError(null);
      if (password) setTransferPassword(password);
      return true;
    } catch (e) {
      if (isTransferPasswordRequired(e.detail)) {
        setPasswordRequired(true);
        return false;
      }
      if (isTransferIncorrectPassword(e.detail)) {
        setPasswordRequired(true);
        setPasswordError("Incorrect password. Please try again.");
        return false;
      }
      if (e.status === 404) setError("Link not found");
      else if (e.status === 410) setError("This link has expired or been deactivated");
      else setError(e.message || "Something went wrong");
      return false;
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      setPasswordRequired(false);
      setPasswordError(null);
      setTransferPassword(null);
      setPayload(null);
      setMode(null);
      setViewingStudy(null);

      try {
        const shareData = await fetchPublicShare(token);
        if (shareData?.study) {
          setPayload(shareData);
          setMode("study");
          setLoading(false);
          return;
        }
        if (shareData?.patient || Array.isArray(shareData?.studies)) {
          setPayload(shareData);
          setMode("patient");
          setLoading(false);
          return;
        }
      } catch (e) {
        if (e.status === 410) {
          setError("This share link has expired or been deactivated");
          setLoading(false);
          return;
        }
        if (e.status === 401) {
          setError("Login required to access this link");
          setLoading(false);
          return;
        }
        if (e.status && e.status !== 404) {
          setError(e.message || "Something went wrong");
          setLoading(false);
          return;
        }
      }

      await loadTransfer();
      setLoading(false);
    };
    load();
  }, [token]);

  const handleTransferPasswordSubmit = async (password) => {
    if (!password?.trim()) {
      setPasswordError("Password is required");
      return;
    }
    setPasswordSubmitting(true);
    setPasswordError(null);
    await loadTransfer(password.trim());
    setPasswordSubmitting(false);
  };

  if (passwordRequired && !payload) {
    return (
      <TransferPasswordGate
        passwordError={passwordError}
        submitting={passwordSubmitting}
        onSubmit={handleTransferPasswordSubmit}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--xl-bg)] flex items-center justify-center">
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--xl-bg)] flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <h2 className="text-lg font-semibold mb-1">{error}</h2>
            <p className="text-sm text-muted-foreground">This link may have expired or been removed by the sender.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (mode === "study" || mode === "patient") {
    if (viewingStudy) {
      const shareFiles = studyAttachments(payload, viewingStudy);
      return (
        <div className="h-screen min-h-0 flex flex-col bg-[var(--xl-bg)]" data-testid="shared-download-page">
          <div className="flex items-center justify-center py-3 px-4 flex-shrink-0" data-testid="shared-brand">
            <XLSyncLogo size="lg" />
          </div>
          <div className="flex-1 min-h-0 px-3 sm:px-4 pb-3">
            <ImageViewer
              shareToken={token}
              shareStudy={viewingStudy}
              shareStudies={[viewingStudy]}
              shareFiles={shareFiles}
              sharePatient={payload?.patient || null}
              onBack={() => setViewingStudy(null)}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-[var(--xl-bg)] py-4" data-testid="shared-download-page">
        <div className="flex items-center justify-center mb-4 px-4" data-testid="shared-brand">
          <XLSyncLogo size="lg" />
        </div>
        <ShareStudyList data={payload} token={token} onOpenStudy={setViewingStudy} />
        <p className="text-center text-[11px] text-muted-foreground mt-4">
          Shared securely via XLSync
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--xl-bg)] px-4 py-4" data-testid="shared-download-page">
      <div className="w-full max-w-6xl mx-auto">
        <div className="flex items-center justify-center mb-4" data-testid="shared-brand">
          <XLSyncLogo size="lg" />
        </div>

        <div className="max-w-lg mx-auto">
          <TransferView transfer={payload} token={token} password={transferPassword} />
        </div>

        <p className="text-center text-[11px] text-muted-foreground mt-4">
          Shared securely via XLSync
        </p>
      </div>
    </div>
  );
}
